import {useQuery} from '@tanstack/react-query';
import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {MAX_NUMBER_OF_ANSWERS} from '../../../../constants';
import {useCodebook} from '../../../../hooks/useCodebook';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {IQuestionAnswerType} from '../../../../incase-domain/elements/base/questionElementBase';
import {IElementMultiSelect} from '../../../../incase-domain/elements/question/multiSelect';
import {fetchCodebooks} from '../../../../service/codebook.service';
import {useSaveAppliedRulesMutation} from '../../../../hooks/useSaveAppliedRules';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {Option} from '../../../../ui/components/common/Select';
import {isRuleInFocus} from '../../../../utils/rule';
import {removeArrayElement, swapArrayElement} from '../../../../utils/utils';
import {TABS} from '../../form/part/tabs';
import DebouncedTextInput from '../common/debounced/DebouncedTextInput';
import ElementShadow from '../common/elementShadow';
import {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import ChangeAnswer from './answers/actions/ChangeAnswer';
import AnswerActions, {AnswerActionType} from './answers/AnswerActions';
import Answers from './answers/Answers';
import AdvancedOptions from './components/advancedOptions/AdvancedOptions';
import QuestionOptions, {QUESTION_OPTIONS} from './components/QuestionOptions';
import QuestionText from './components/QustionText';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedMultiSelectElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedMultiSelectElementComponent(props: HighlightedMultiSelectElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {mutate: saveAppliedRules} = useSaveAppliedRulesMutation();
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IElementMultiSelect;
    const updateHighlightedElement = useSetHighlightedElement();
    const presentation = useSelector(selectedPresentationSelector);
    const customType = 'CUSTOM';

    const {codebookId} = highlightedElement;

    const {data: codebooks} = useQuery(QUERY_KEYS.codebook.sub('availableInMultiselect'), () => fetchCodebooks(true));
    const customOption = {
        value: customType,
        label: t('presEditPage.editSidebar.elements.multiselectQuestion.customAnswers'),
    };
    const codebookOptions = [customOption, ...(codebooks?.map((cb) => ({value: cb._id, label: cb.name})) || [])];
    const selectedCodebookOption = codebookId ? codebookOptions.find((o) => o.value === highlightedElement.codebookId) : customOption;
    const noOfCodebookEntries = codebooks?.find((cb) => cb._id === codebookId)?.noOfEntries || 0;

    const {answersRef, searchAnswers, setSearchAnswers, codebookAnswers, refetch} = useCodebook(codebookId || '', highlightedElement);

    const answers = codebookId ? codebookAnswers : highlightedElement.answers;

    const handleCodebookChange = async (option: Option): Promise<void> => {
        setSearchAnswers('');
        if (option.value === customType) {
            updateHighlightedElement.multiple(
                ['codebookId', 'answers'],
                [
                    undefined,
                    [
                        {text: 'Answer 1', type: 'incorrect', comment: ''},
                        {text: 'Answer 2', type: 'incorrect', comment: ''},
                    ],
                ]
            );
        } else {
            refetch();
            updateHighlightedElement.multiple(['codebookId', 'answers'], [option.value, []]);
        }
    };

    function addAnswer(): void {
        if (highlightedElement.answers.length < MAX_NUMBER_OF_ANSWERS) {
            const newAnswers = [
                ...highlightedElement.answers,
                {
                    text: `${t('presEditPage.editSidebar.elements.initialStates.answer')} ${highlightedElement.answers.length + 1}`,
                    type: highlightedElement.isDebatable ? 'debatable' : 'incorrect',
                    comment: '',
                },
            ];
            updateHighlightedElement.single('answers', newAnswers);
        } else {
            toast.info(`${t('toasts.maxAnswers')}`);
        }
    }

    function removeAnswer(index: number): void {
        const newAnswers = removeArrayElement(highlightedElement.answers, index);
        const updatedRules = presentation.appliedRules
            ? presentation.appliedRules.filter((r) => !isRuleInFocus(r, highlightedElement.id, index))
            : [];

        updateHighlightedElement.single('answers', newAnswers);
        saveAppliedRules({appliedRules: updatedRules, presentationId: presentation._id});
    }

    function answerChangeHandler(index: number, value: string): void {
        const changedAnswer = {...highlightedElement.answers[index]};
        changedAnswer.text = value;

        const newAnswers = swapArrayElement(highlightedElement.answers, index, changedAnswer);
        updateHighlightedElement.single('answers', newAnswers);
    }

    function changeAllAnswersTo(type: IQuestionAnswerType): void {
        const newAnswers = highlightedElement.answers.map((answer) => ({
            ...answer,
            type,
        }));
        const isDebatable = type === 'debatable';
        updateHighlightedElement.multiple(['answers', 'defaultCodebookAnswerType', 'isDebatable'], [newAnswers, type, isDebatable]);
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <AdvancedOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    answerColorFields={[COLOR_FIELDS.CORRECT_COLOR, COLOR_FIELDS.INCORRECT_COLOR]}
                />
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <QuestionOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    questionOptions={[
                        QUESTION_OPTIONS.SINGLE_CORRECT,
                        QUESTION_OPTIONS.SHOW_IN_REPORT,
                        QUESTION_OPTIONS.RETRY_QUESTION,
                        QUESTION_OPTIONS.BLOCKING_QUESTION,
                    ]}
                />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <QuestionText
                highlightedElement={highlightedElement}
                errors={errors}
                highlightedElementChangeHandler={updateHighlightedElement}
                showDebatable
            />
            <Answers
                errors={errors}
                addAnswer={codebookId ? undefined : addAnswer}
                noOfAnswers={codebookId ? noOfCodebookEntries : answers.length}
                header={
                    <DropdownMenuCompact
                        menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                        fixed="left"
                        items={[
                            [
                                {
                                    label: t('presEditPage.editSidebar.elements.multiselectQuestion.codebooks'),
                                    items: [
                                        codebookOptions.map((el) => ({
                                            label: el.label,
                                            onClick: () => {
                                                handleCodebookChange(el);
                                            },
                                            disabled: el.value === highlightedElement.codebookId,
                                        })),
                                    ],
                                },
                                {
                                    label: t('presEditPage.editSidebar.elements.multiselectQuestion.markAll'),
                                    items: [
                                        [
                                            {
                                                label: t('designSidepanel.correct'),
                                                onClick: () => {
                                                    changeAllAnswersTo('correct');
                                                },
                                            },
                                            {
                                                label: t('designSidepanel.incorrect'),
                                                onClick: () => {
                                                    changeAllAnswersTo('incorrect');
                                                },
                                            },
                                            {
                                                label: t('designSidepanel.debatable'),
                                                onClick: () => {
                                                    changeAllAnswersTo('debatable');
                                                },
                                            },
                                        ],
                                    ],
                                },
                            ],
                        ]}
                    >
                        <Button
                            className="c-sequenceQuestionElementOrder__shuffleButton"
                            variant="ghost"
                            color="primary"
                            leading="ph:dots-three-vertical-bold"
                            padded={false}
                        />
                    </DropdownMenuCompact>
                }
            >
                {selectedCodebookOption !== customOption && (
                    <div className="u-mb-2">
                        <DebouncedTextInput
                            className="c-sidepanel__input"
                            onChange={(value: string) => setSearchAnswers(value)}
                            value={searchAnswers}
                            placeholder={t('designSidepanel.searchAnswers')}
                        />
                    </div>
                )}

                {/* <div className="c-newAnswer__lineBreak">
                    <div className="c-newAnswer__lineBreak_line" />
                    <div className="c-newAnswer__lineBreak_text">{t('designSidepanel.answers')}</div>
                    <div className="c-newAnswer__lineBreak_line" />
                </div> */}

                <div ref={answersRef} className="c-newAnswer__container">
                    {answers?.map((answer, index) => (
                        <React.Fragment key={index}>
                            <ChangeAnswer
                                answer={answer.text}
                                validationErrors={errors}
                                answerChangeHandler={answerChangeHandler}
                                index={index}
                                inactive={!!codebookId}
                                highlightedElement={highlightedElement}
                                highlightedElementChangeHandler={updateHighlightedElement}
                                codebookAnswer={codebookId ? answer : undefined}
                            />

                            {!answer.numberOfChildren && (
                                <AnswerActions
                                    index={index}
                                    validationErrors={errors}
                                    highlightedElement={highlightedElement}
                                    highlightedElementChangeHandler={updateHighlightedElement}
                                    actions={[
                                        AnswerActionType.ANSWER_RULES,
                                        AnswerActionType.CHANGE_COMMENT,
                                        ...(codebookId ? [] : [AnswerActionType.REMOVE_ANSWER]),
                                    ]}
                                    removeAnswer={removeAnswer}
                                    codebookAnswer={codebookId ? answer : undefined}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </Answers>
        </>
    );
}

export default HighlightedMultiSelectElementComponent;
